import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Activity,
  ActivityData,
  ActivityDataInput,
  NewActivity
} from "@/entities/activities/types";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import FormNew from "@/components/controls/Form/FormNew";
import { FormSectionProps } from "@/components/controls/Form/FormSection";
import { FormFieldNewType } from "@/components/controls/Form/types";
import Modal from "@/components/controls/Modal";
import Text from "@/components/ui/text";
import { getEnumTKey } from "@/utils/strings";
import Tabs from "@/components/controls/Tabs";
import { EntityType } from "@/services/hooks/entities/useEntityType";
import { commonRules } from "@/utils/validation";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";
import { EntityData } from "@/services/api/types";
import { multiSelectionOptions } from "@/components/controls/MultiSelect";
import { getTagColorByName } from "@/utils/tags";
import { useActivityTags } from "@/pages/settings/components/activities/hooks";
import { useActivityModal } from "@/components/layouts/Activities/hooks/useActivityModal";
import type { ActivitySubmissionPayload } from "@/components/layouts/Activities/hooks/useActivityModalSubmission";
import { mergeActivitySubmissionNotes } from "@/components/layouts/Activities/hooks/useActivityModalSubmission";
import { getUTCDateTime } from "@/utils/datetime";
import { forEach, isUndefined, map } from "lodash";
import CreatePersonModal from "@/components/layouts/CreatePersonModal";
import { getMustSelectCompanyText } from "@/entities/activities/helpers";

interface Props {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  entityType: EntityType;
  entityData: EntityData | undefined;
  pageName: string;
  isEditMode: boolean;
  editActivityData?: ActivityData | null;
  resetPage?: () => void;
}

const MeetingActivityModal: React.FC<Props> = ({
  isOpen,
  setIsOpen,
  entityType,
  entityData,
  pageName,
  isEditMode,
  editActivityData,
  resetPage
}) => {
  const { t } = useTranslation();

  // Tag lists are per meeting type, so the type has to be known from the very
  // first render in edit mode — starting at the online default and correcting
  // it afterwards makes the tag field hydrate against the wrong type.
  const initialMeetingType = (
    isEditMode && editActivityData?.type !== undefined
      ? editActivityData.type
      : Activity.ONLINE_MEETING
  ).toString();

  const [meetingType, setMeetingType] = useState(initialMeetingType);
  // The edited activity arrives as a fresh object on every refetch, so syncing
  // the type from it has to stop once the user has picked one by hand —
  // otherwise a background refetch reverts their choice mid-edit.
  const [isMeetingTypeUserSet, setIsMeetingTypeUserSet] = useState(false);
  const [startTime, setStartTime] = useState("");

  const activityTags = useActivityTags(
    Number(meetingType),
    Boolean(meetingType)
  );

  const getMeetingTypeOptions = () => {
    let options: { [key: string]: string } = {};
    forEach(
      [
        Activity.ONLINE_MEETING.toString(),
        Activity.IN_PERSON_MEETING.toString()
      ],
      (type) => {
        options[type] = t(
          getEnumTKey("activities", "meetingTypes", Activity, Number(type))
        );
      }
    );
    return options;
  };

  const {
    formRef,
    isFormValid,
    setIsFormValid,
    formSetValue,
    handleSetValue,
    activityData,
    isCompanySet,
    handleSubmit: baseHandleSubmit,
    getBaseFields,
    processFieldsForEditMode,
    isCreatePersonModalOpen,
    setIsCreatePersonModalOpen,
    onPersonCreation,
    effectiveCompanyId
  } = useActivityModal({
    activityType: NewActivity.MEETING,
    isOpen,
    setIsOpen,
    entityType,
    entityData,
    pageName,
    isEditMode,
    editActivityData,
    resetPage
  });

  const newActivityKey = getEnumTKey(
    "activities",
    "newActivities",
    NewActivity,
    NewActivity.MEETING
  );

  // Only a user switching the type invalidates the current tags — the tag list
  // is per type. Syncing the type to the edited activity must leave them alone.
  const handleMeetingTypeChange = useCallback(
    (value: string) => {
      if (value === meetingType) {
        return;
      }
      setMeetingType(value);
      setIsMeetingTypeUserSet(true);
      if (formSetValue) {
        formSetValue("tags", [], { shouldValidate: true });
      }
    },
    [meetingType, formSetValue]
  );

  const getTagsOptions = (): multiSelectionOptions => {
    return map(activityTags.data || [], (tag) => ({
      label: tag.name,
      value: tag.id.toString(),
      badgeColor: getTagColorByName(tag.name)
    }));
  };

  const formSections = useMemo<FormSectionProps[]>(() => {
    const baseFields = getBaseFields();

    const meetingFields: { [key: string]: FormFieldNewType } = {
      startTime: {
        name: "startTime",
        label: t("activities.activityModal.time"),
        type: "time",
        required: true,
        layout: "half",
        labelPosition: "left",
        fieldActionProps: {
          placeholder: t("activities.activityModal.startTime"),
          onChange: (value: string) => setStartTime(value)
        } as any
      },
      endTime: {
        name: "endTime",
        label: "",
        type: "time",
        required: true,
        layout: "half",
        showRequiredIndicator: false,
        labelPosition: "left",
        fieldActionProps: {
          placeholder: t("activities.activityModal.endTime"),
          nearestTime: startTime
        } as any,
        validation: {
          customRules: [
            commonRules.timeRange(t("activities.activityModal.endTimeError"))
          ]
        }
      },
      tags: {
        name: "tags",
        label: t("activities.activityModal.tags"),
        type: "multiSelect",
        required: false,
        labelPosition: "left",
        fieldActionProps: {
          placeholder: t("activities.activityModal.addTags"),
          options: getTagsOptions() || [],
          modalPopover: true
        } as any,
        defaultValue: activityData?.tags
      },
      location: {
        name: "location",
        label: t("activities.activityModal.location"),
        type: "text",
        required: false,
        labelPosition: "left",
        fieldActionProps: {} as any
      },
      summary: {
        name: "summary",
        label: t("activities.activityModal.meetingSummary"),
        type: "textEditor",
        required: false,
        labelPosition: "top",
        fieldActionProps: {
          placeholder: t("activities.activityModal.meetingSummaryPlaceholder")
        }
      }
    };

    const timeFieldNames = ["date", "startTime", "endTime"];
    const defaultFields = ["subject", "tags", "participants", "summary"];

    let fieldNames: string[] = [];
    switch (meetingType) {
      case Activity.IN_PERSON_MEETING.toString():
        fieldNames = [...timeFieldNames, "location", ...defaultFields];
        break;
      case Activity.ONLINE_MEETING.toString():
        fieldNames = [...timeFieldNames, ...defaultFields];
        break;
      default:
        fieldNames = [...timeFieldNames, ...defaultFields];
    }

    const allFields = { ...baseFields, ...meetingFields };
    let fields = fieldNames
      .map((name) => allFields[name as keyof typeof allFields])
      .filter(Boolean);

    fields = processFieldsForEditMode(fields);

    const timeFields = fields.filter((field) =>
      timeFieldNames.includes(field.name)
    );

    const otherFields = fields.filter(
      (field) => !timeFieldNames.includes(field.name)
    );

    return [
      {
        id: "meeting-time-section",
        fields: timeFields
      },
      {
        id: "meeting-type-section",
        fields: [],
        bottomCustomComponent: (
          <div className="flex flex-row items-start gap-4 w-full">
            <Text variant="bodySecondary" className="w-[200px] shrink-0 pt-2">
              {t("activities.activityModal.meetingType")}
            </Text>
            <div className="flex-1">
              <Tabs
                dataTest="meeting-type-tabs"
                options={getMeetingTypeOptions()}
                value={meetingType}
                valueSelected={handleMeetingTypeChange}
                className="w-full border-border-border2"
                size="lg"
              />
            </div>
          </div>
        )
      },
      {
        id: "meeting-details-section",
        fields: otherFields
      }
    ];
  }, [
    meetingType,
    startTime,
    getBaseFields,
    processFieldsForEditMode,
    t,
    activityTags.data,
    isEditMode,
    handleMeetingTypeChange
  ]);

  useEffect(() => {
    if (
      isEditMode &&
      !isMeetingTypeUserSet &&
      !isUndefined(activityData) &&
      !isUndefined(activityData.type) &&
      formSetValue
    ) {
      setMeetingType(activityData.type.toString());
    }
  }, [isEditMode, isMeetingTypeUserSet, activityData, formSetValue]);

  useEffect(() => {
    if (!isOpen) {
      setMeetingType(initialMeetingType);
      setIsMeetingTypeUserSet(false);
      // The same modal instance is reused across a close/reopen of one
      // activity, and the form's native reset leaves controlled fields alone —
      // so a type switch the user cancelled would keep its tag clear and the
      // next save would drop tags that were never edited.
      if (isEditMode && formSetValue) {
        formSetValue("tags", editActivityData?.tags ?? [], {
          shouldValidate: true
        });
      }
    }
  }, [
    isOpen,
    isEditMode,
    initialMeetingType,
    editActivityData?.tags,
    formSetValue
  ]);

  useEffect(() => {
    if (typeof formSetValue === "function") {
      // This will trigger form re-render with updated nearestTime for endTime field
    }
  }, [startTime, formSetValue]);

  const handleSubmit = async (data: ActivityDataInput) => {
    const customTransform = (
      baseData: ActivitySubmissionPayload
    ): ActivitySubmissionPayload => {
      const transformed = mergeActivitySubmissionNotes(baseData, {
        ...(!isEditMode ? { meetingContent: "" } : {}),
        startTime: data?.startTime
          ? getUTCDateTime(data.date, data.startTime)
          : undefined,
        endTime: data?.endTime
          ? getUTCDateTime(data.date, data.endTime)
          : undefined,
        ...(isEditMode && Number(meetingType) === Activity.ONLINE_MEETING
          ? { location: null }
          : {})
      });

      return {
        ...transformed,
        type: Number(meetingType)
      };
    };

    await baseHandleSubmit(data, customTransform);
  };

  const handleCancel = () => {
    setIsOpen(false);
  };

  const handleCreateNew = () => {
    formRef.current?.requestSubmit();
  };

  return (
    <>
      <Modal
        open={isOpen}
        setIsOpen={setIsOpen}
        contentClassname="w-[35rem]"
        title={
          <Text as="div" variant="lgSubtitleH1">
            {t(`activities.activityModal.${isEditMode ? "edit" : "add"}`, {
              type: t(newActivityKey).toLowerCase(),
              activity: ""
            })}
          </Text>
        }
        content={
          <div
            className={`flex flex-col gap-2 w-[35rem] p-6 h-fit overflow-y-auto max-h-[75vh]`}
          >
            {/* Hidden element to capture initial focus */}
            <div tabIndex={0} className="sr-only" />
            <FormNew
              sections={formSections}
              isFormValid={(isValid) => {
                setIsFormValid(isValid);
              }}
              onSubmit={handleSubmit}
              formRef={formRef}
              onSetValue={handleSetValue}
            />
          </div>
        }
        footer={
          <div className="flex justify-end w-full">
            <div className="flex gap-2">
              <Button
                data-test="cancel-button"
                variant="secondary"
                onClick={handleCancel}
              >
                {t("cancel")}
              </Button>
              <Tooltip>
                <TooltipTrigger>
                  <Button
                    data-test="submit-button"
                    disabled={!isFormValid || !isCompanySet}
                    onClick={handleCreateNew}
                  >
                    {t(isEditMode ? "save" : "activities.activityModal.add", {
                      type: t(newActivityKey).toLowerCase(),
                      activity: ""
                    })}
                  </Button>
                </TooltipTrigger>
                {!isCompanySet && (
                  <TooltipContent maxWidth={15}>
                    {getMustSelectCompanyText(t, entityType)}
                  </TooltipContent>
                )}
              </Tooltip>
            </div>
          </div>
        }
      />
      <CreatePersonModal
        isOpen={isCreatePersonModalOpen}
        setIsOpen={setIsCreatePersonModalOpen}
        onSuccess={onPersonCreation}
        companyId={effectiveCompanyId}
      />
    </>
  );
};

export default MeetingActivityModal;
